// @ts-strict-ignore

import { FC, useMemo, useState, MouseEvent } from 'react';

import { Box } from '@mui/material';

import { AnalyticEventAction } from 'analytics';
import { trackActionButtonAnalyticsEvent } from 'analytics/events/action-button';
import { Dialogs } from 'analytics/events/dialog';
import { trackSectionUsageAnalyticsEvent } from 'analytics/events/section-usage';
import { observer } from 'mobx-react';

import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { sharedTestSelectors } from 'selectors/shared.selectors';
import { workQueueOverdueSectionSelectors } from 'selectors/work-queue-overdue-section.selectors';

import { useStores } from 'mobx/hooks/useStores';

import { showToast } from 'utils/UserMessageUtils';

import Patient from 'models/Patient';
import ScheduledProtocol, { ProtocolName } from 'models/ScheduledProtocol';

import { useToggle } from 'hooks/useToggle';
import { useWqOpenItemsSection } from 'hooks/useWqOpenItemsSection';

import SmsBlockedPopup from 'views/Modals/SmsBlockedPopup';
import { StyledSectionActionsContainer } from 'views/WorkQueue/WorkQueue.styled';
import { WorkQueueTabName } from 'views/WorkQueue/WorkQueue.types';

import { TicketSectionId } from 'views/WorkQueue/WorkQueue.types';

import WqCollapsibleSection from 'views/WorkQueue/WorkQueueOpenItemsView/WqCollapsibleSection';
import { WqSectionControl } from 'views/WorkQueue/WorkQueueOpenItemsView/WqSectionControl';
import { WqTicketPlaceholder } from 'views/WorkQueue/WqTicketPlaceholder';

import { CardRow } from 'components/Surfaces/Card';
import OverduePatientReportRow from 'components/Ticket/TicketRow/OverduePatientReportRow';
import { Section } from 'components/Ticket/TicketsContainers/TicketsContainers.constants';
import { useSmsModal } from 'components/Ticket/TicketsContainers/TicketsContainers.shared';
import { SimpleTooltip } from 'components/Tooltip';
import { OutlinedButton } from 'components/UIkit/atoms/Button';
import { MessageDialog } from 'components/UIkit/atoms/Dialog';

const WqOverdueReportsSection: FC = () => {
  const { items, totalItems, totalPages, currentPage, isSectionPageLoading } =
    useWqOpenItemsSection(TicketSectionId.OverdueReports);
  const { workQueueStore } = useStores();
  const [isSnoozeAllOverdueReportsLoading, setIsSnoozeAllOverdueReportsLoading] = useState(false);
  const [isSnoozeAllOverdueReportsPopupOpen, setIsSnoozeAllOverdueReportsPopupOpen] =
    useState(false);
  const [smsModal, toggleSmsModal] = useSmsModal();
  const smsBlockedPopup = useToggle(false);

  // Find eligible patient IDs for 'snooze all' action
  const patientIdsToSnoozeAllAction = useMemo(
    () =>
      workQueueStore.sectionDataMap[TicketSectionId.OverdueReports].items
        // only include patients that are not snoozed and are eligible for snoozing
        .filter((overdueItem) => {
          const patient = workQueueStore.patientsMap.get(overdueItem.itemData.patientId)!;

          if (patient.hasPatientSnoozed || !patient.mainScheduledProtocol) {
            return false;
          }

          const protocol = patient.mainScheduledProtocol;

          // Include Symptom and Oral protocols
          if (
            protocol.name === ProtocolName.symptom ||
            protocol.name === ProtocolName.oralOncolytics
          ) {
            return true;
          }

          // Include DrugSpecific protocols, excluding hourly protocols and hospitalized patients
          if (protocol.name === ProtocolName.drugSpecific) {
            const isHourlyProtocol =
              protocol.code === ScheduledProtocol.PROTOCOL_CODE_MULTIPLE_HOURLY;
            return !isHourlyProtocol && !patient.isHospitalized;
          }

          return false;
        })
        .map((overdueItem) => overdueItem.itemData.patientId),
    [workQueueStore.sectionDataMap, workQueueStore.patientsMap]
  );

  const onSnoozeAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (patientIdsToSnoozeAllAction.length > 0) {
      setIsSnoozeAllOverdueReportsPopupOpen(true);
    }
  };

  const handleSnoozeAll = async () => {
    trackActionButtonAnalyticsEvent({
      action: AnalyticEventAction.SnoozeAll,
      value: workQueueStore.sectionDataMap[TicketSectionId.OverdueReports].totalItems,
      type: 'by protocol',
      page_number: currentPage
    });

    setIsSnoozeAllOverdueReportsLoading(true);

    try {
      await workQueueStore.sendBulkSnoozeRequest(patientIdsToSnoozeAllAction);

      workQueueStore.setSectionCurrentPage(TicketSectionId.OverdueReports, 0);
      workQueueStore.handleSectionPageChange(TicketSectionId.OverdueReports);
    } catch (error) {
      showToast({
        message: 'Failed to snooze patients',
        description: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
    } finally {
      setIsSnoozeAllOverdueReportsLoading(false);
      setIsSnoozeAllOverdueReportsPopupOpen(false);
    }
  };

  return (
    <>
      <MessageDialog
        id={Dialogs.SnoozeAllOverdueReports}
        testHook={workQueueOverdueSectionSelectors.snoozeAllPopup}
        isOpen={isSnoozeAllOverdueReportsPopupOpen}
        handleClose={() => setIsSnoozeAllOverdueReportsPopupOpen(false)}
        title={`Snooze ${patientIdsToSnoozeAllAction.length} Overdue Reports`}
        primaryActionProps={{
          text: isSnoozeAllOverdueReportsLoading ? 'Sending...' : 'Snooze All',
          disabled: isSnoozeAllOverdueReportsLoading,
          onClick: handleSnoozeAll
        }}
        secondaryActionProps={{
          text: 'Cancel',
          onClick: () => setIsSnoozeAllOverdueReportsPopupOpen(false)
        }}
      >
        Are you sure you want to snooze all eligible patients on the current page of the “Overdue
        Reports” section?
      </MessageDialog>

      <div className="report-section">
        {smsModal}
        <SmsBlockedPopup isOpen={smsBlockedPopup.isOpen} onCancelClicked={smsBlockedPopup.toggle} />

        <WqCollapsibleSection
          sectionName={Section.Overdue}
          totalItems={totalItems}
          onTrigger={(isOpen) =>
            trackSectionUsageAnalyticsEvent({
              action: isOpen ? AnalyticEventAction.Collapse : AnalyticEventAction.Expand,
              value: Section.Overdue,
              page_number: currentPage,
              tab: WorkQueueTabName.OpenItems
            })
          }
          testHook={sharedTestSelectors.ticketSection.container(TicketSectionId.OverdueReports)}
        >
          <StyledSectionActionsContainer>
            <SimpleTooltip
              disabled={patientIdsToSnoozeAllAction.length > 0}
              title={
                <Box p={16}>
                  No patients available to snooze on this page. All have been snoozed or are not
                  eligible for snoozing.
                </Box>
              }
              placement="bottom"
            >
              <OutlinedButton
                onClick={onSnoozeAll}
                testHook={workQueueOverdueSectionSelectors.snoozeAllButton}
                disabled={patientIdsToSnoozeAllAction.length === 0}
              >
                Snooze All
              </OutlinedButton>
            </SimpleTooltip>

            {totalPages > 1 && <WqSectionControl sectionName={TicketSectionId.OverdueReports} />}
          </StyledSectionActionsContainer>

          <div className="tickets-list">
            <TransitionGroup>
              {items.map(({ itemData }, index) => {
                const patient = workQueueStore.getPatientModel(itemData.patientId);

                return (
                  <CSSTransition
                    key={itemData.patientId}
                    classNames="connect-ticket-transition"
                    timeout={600}
                  >
                    {isSectionPageLoading ? (
                      <WqTicketPlaceholder />
                    ) : (
                      <CardRow>
                        <OverduePatientReportRow
                          patient={patient as Patient}
                          toggleSmsModal={(shouldOpenSmsBlockedPopup, patient) =>
                            shouldOpenSmsBlockedPopup
                              ? smsBlockedPopup.toggle()
                              : toggleSmsModal(patient)
                          }
                          withPatientLink
                          ticketIndex={index}
                          ticketSectionCurrentPage={currentPage - 1}
                        />
                      </CardRow>
                    )}
                  </CSSTransition>
                );
              })}
            </TransitionGroup>
          </div>

          {totalPages > 1 && (
            <WqSectionControl sectionName={TicketSectionId.OverdueReports} isBottom />
          )}
        </WqCollapsibleSection>
      </div>
    </>
  );
};

export default observer(WqOverdueReportsSection);
